import React from 'react';
import { Form, Radio, Button, Checkbox, App, Tabs, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormikValues, FormikHelpers } from 'formik';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import FormInput from '../../global/Forms/FormInput';
import CopyEBPTemplateModal from './CopyEBPTemplateModal';
import EditSheetModal from './EditSheetModal';
import { ExportBuilderAdvancedSheet } from '../../../../types/export_builder_advanced';
import { DEFAULT_SHEET_NAME } from '../../../constants/ImportExportConstants';

type ExportBuilderAdvancedEditProps = {
  selectedTemplateId: number;
  values: FormikValues;
  hasApiAccess: boolean;
  isReceiver?: boolean;
  setFieldValue: FormikHelpers<FormikValues>['setFieldValue'];
  handleDeleteTemplate: () => void;
  handleCopyTemplate: (props: {
    newTitle: string;
    columnIds: number[];
    brandIds?: number[];
  }) => Promise<any>;
  onActiveSheetChange?: (sheetId: string | null, isUserSelection?: boolean) => void;
  templateSheets: ExportBuilderAdvancedSheet[];
  onCreateSheet: (sheetName: string) => Promise<any>;
  onDeleteSheet: (sheetId: number) => Promise<any>;
  onRenameSheet: (sheetId: number, newName: string) => Promise<any>;
  onToggleMultiSheet?: (
    isEnabled: boolean,
    preserveSheetName?: string,
    currentColumns?: any[]
  ) => Promise<void>;
  activeSheetId: number | null;
  usedEbpIds: number[];
  pdmUser: boolean;
};

const ExportBuilderAdvancedEdit: React.FC<ExportBuilderAdvancedEditProps> = ({
  selectedTemplateId,
  values,
  hasApiAccess,
  isReceiver,
  setFieldValue,
  handleDeleteTemplate,
  handleCopyTemplate,
  onActiveSheetChange,
  templateSheets,
  onCreateSheet,
  onDeleteSheet,
  onRenameSheet,
  onToggleMultiSheet,
  activeSheetId,
  usedEbpIds,
  pdmUser,
}) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();

  const [showCopyModal, setShowCopyModal] = React.useState(false);
  const [showEditSheetModal, setShowEditSheetModal] = React.useState(false);
  const [editSheetName, setEditSheetName] = React.useState('');
  const [editSheetId, setEditSheetId] = React.useState<number | null>(null);
  const [isEditMode, setIsEditMode] = React.useState(false);

  const isEbpInUse = usedEbpIds.includes(selectedTemplateId);
  const isMultiSheet = Boolean(values?.multiSheetEnabled);
  const sheets = React.useMemo(() => {
    return isMultiSheet && templateSheets && templateSheets.length > 0
      ? [...templateSheets].sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
      : [];
  }, [isMultiSheet, templateSheets]);

  const handleFileTypeChange = (value: number) => {
    if (value === 1 && isMultiSheet) {
      modal.confirm({
        title: t('exportBuilderAdvanced:warning'),
        content: t('exportBuilderAdvanced:switchToCsvWarning'),
        okText: t('exportBuilderAdvanced:continue'),
        cancelText: t('common:cancel'),
        onOk() {
          setFieldValue('fileTypeId', 1);
          setFieldValue('multiSheetEnabled', false);
        },
      });
    } else {
      setFieldValue('fileTypeId', value);
      if (value === 1) {
        setFieldValue('multiSheetEnabled', false);
      }
    }
  };

  const handleAddSheet = () => {
    const newSheetIndex = sheets.length + 1;
    setEditSheetName(`Sheet ${newSheetIndex}`);
    setEditSheetId(null);
    setIsEditMode(false);
    setShowEditSheetModal(true);
  };

  const handleEditSheetConfirm = async (sheetName: string) => {
    if (isEditMode && editSheetId) {
      await onRenameSheet(editSheetId, sheetName.trim());
    } else {
      const response = await onCreateSheet(sheetName.trim());
      const newSheet = response.value.data;
      onActiveSheetChange?.(newSheet.id.toString(), true);
    }

    setShowEditSheetModal(false);
    setEditSheetName('');
    setEditSheetId(null);
    setIsEditMode(false);
  };

  const handleEditSheetCancel = () => {
    setShowEditSheetModal(false);
    setEditSheetName('');
    setEditSheetId(null);
    setIsEditMode(false);
  };

  const handleDeleteSheet = (sheetId: number) => {
    if (sheets.length <= 1) {
      return;
    }

    modal.confirm({
      title: t('exportBuilderAdvanced:deleteThisSheet'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      okButtonProps: { danger: true },
      async onOk() {
        await onDeleteSheet(sheetId);

        if (activeSheetId === sheetId) {
          const remainingSheets = sheets.filter(s => s.id !== sheetId);
          if (remainingSheets.length > 0) {
            const newActiveSheet = remainingSheets[0];
            const newActiveSheetId =
              typeof newActiveSheet.id === 'string'
                ? parseInt(newActiveSheet.id, 10)
                : newActiveSheet.id;
            onActiveSheetChange?.(newActiveSheetId.toString(), true);
          }
        }
      },
    });
  };

  const handleSheetClick = (sheetId: number) => {
    onActiveSheetChange?.(sheetId.toString(), true);
  };

  const handleEditSheet = (sheetId: number, sheetName: string) => {
    setEditSheetId(sheetId);
    setEditSheetName(sheetName);
    setIsEditMode(true);
    setShowEditSheetModal(true);
  };

  return (
    <>
      <div className="flex items-center p-4">
        <Form layout="inline" labelAlign="left" className="flex">
          <div className="flex mr-6">
            <FormInput
              testId="template-name"
              label={t('common:name')}
              name="templateName"
              disabled={isEbpInUse && !pdmUser}
              addonAfter={
                selectedTemplateId && hasApiAccess
                  ? `${t('common:id')}: ${selectedTemplateId}`
                  : undefined
              }
            />
          </div>

          <div className="flex mr-6">
            <Form.Item label={t('exportBuilderAdvanced:exportType')}>
              <div className="mr-4">
                <Radio.Group
                  onChange={e => handleFileTypeChange(e.target.value)}
                  value={values.fileTypeId}
                  disabled={isEbpInUse && !pdmUser}
                >
                  <Radio.Button value={2}>{t('glossary:excel')}</Radio.Button>
                  <Radio.Button value={1}>{t('glossary:csv')}</Radio.Button>
                </Radio.Group>
              </div>
            </Form.Item>
          </div>
          {values.fileTypeId === 1 && (
            <div className="flex items-center mr-6">
              <FormInput
                testId="csv-identifier"
                label={t('exportBuilderAdvanced:csvDelimiter')}
                name="csvSeparator"
                disabled={isEbpInUse && !pdmUser}
              />
            </div>
          )}
          {values.fileTypeId === 2 && !isMultiSheet && (
            <div className="flex items-center mr-6">
              <FormInput
                label={t('exportBuilderAdvanced:sheetName')}
                name="sheetName"
                disabled={isEbpInUse && !pdmUser}
              />
            </div>
          )}
          {values.fileTypeId === 2 && !isReceiver && (
            <div className="flex items-center mr-6">
              <Checkbox
                checked={isMultiSheet}
                disabled={isEbpInUse && !pdmUser}
                onChange={async e => {
                  const isEnabled = e.target.checked;
                  const currentSheetName = values.sheetName || DEFAULT_SHEET_NAME;

                  if (onToggleMultiSheet) {
                    await onToggleMultiSheet(isEnabled, currentSheetName, values.templateColumns);
                  }
                }}
              >
                {t('exportBuilderAdvanced:multiSheetFile')}
              </Checkbox>
            </div>
          )}
        </Form>

        <div className="flex flex-1">
          <div className="inline-flex">
            <Button onClick={() => setShowCopyModal(true)}>
              {t('exportBuilderAdvanced:copyTemplate')}
            </Button>
          </div>
        </div>

        <div className="flex gap-2" style={{ marginLeft: 'auto', paddingLeft: '24px' }}>
          {isMultiSheet && sheets.length > 1 && activeSheetId && (
            <Button
              onClick={() => handleDeleteSheet(activeSheetId)}
              danger
              ghost
              data-testid="delete-sheet"
              disabled={isEbpInUse && !pdmUser}
            >
              {t('exportBuilderAdvanced:deleteSheet')}
            </Button>
          )}
          <Tooltip
            title={
              isEbpInUse
                ? t('exportBuilderAdvanced:cannotDeleteEbpInUse')
                : t('exportBuilderAdvanced:deleteTemplate')
            }
          >
            <Button
              onClick={() => handleDeleteTemplate()}
              danger
              ghost
              data-testid="delete-template"
              disabled={isEbpInUse && !pdmUser}
            >
              {t('exportBuilderAdvanced:deleteTemplate')}
            </Button>
          </Tooltip>
        </div>
      </div>

      {values.fileTypeId === 2 && isMultiSheet && (
        <div className="px-4 pb-2">
          <div className="flex items-center justify-between">
            <Tabs
              activeKey={activeSheetId?.toString()}
              onChange={key => handleSheetClick(Number(key))}
              type="card"
              items={sheets.map(sheet => ({
                key: sheet.id.toString(),
                label: (
                  <span>
                    {sheet?.name}
                    {activeSheetId === Number(sheet.id) && (
                      <EditOutlined
                        className="ml-2"
                        onClick={() => handleEditSheet(Number(sheet.id), sheet.name)}
                      />
                    )}
                  </span>
                ),
                children: <div />,
              }))}
            />
            <Button onClick={handleAddSheet} type="primary" ghost icon={<PlusOutlined />}>
              {t('exportBuilderAdvanced:addSheetButton')}
            </Button>
          </div>
        </div>
      )}

      <CopyEBPTemplateModal
        showModal={showCopyModal}
        isReceiver={isReceiver}
        onCancel={() => setShowCopyModal(false)}
        onClose={() => setShowCopyModal(false)}
        handleCopy={(name: string, columnIds: number[], brandIds?: number[]) =>
          handleCopyTemplate({ newTitle: name, brandIds, columnIds })
        }
        isMultiSheet={isMultiSheet}
        activeSheetName={activeSheetId ? sheets.find(s => s.id === activeSheetId)?.name : undefined}
      />

      <EditSheetModal
        showModal={showEditSheetModal}
        sheetName={editSheetName}
        existingSheetNames={sheets.map(s => s.name)}
        currentSheetName={isEditMode ? sheets.find(s => s.id === editSheetId)?.name : undefined}
        onSheetNameChange={setEditSheetName}
        onCancel={handleEditSheetCancel}
        onConfirm={handleEditSheetConfirm}
        isEditMode={isEditMode}
      />
    </>
  );
};

export default ExportBuilderAdvancedEdit;
